// The Penyedia paket stages as Simulasi Pluto v4 shows them
// (supplierProjectPage): four stage tabs any of which can be opened, and per
// stage its numbered sections, with the stage's checklist folded under them
// ("Detail aktivitas relevan"), except Serah Terima, where the checklist is
// the work itself.
//
//   Persiapan Pemilihan    1 Cari Paket Tayang (Pantau Paket)
//                          2 Data Dasar Paket dan Keputusan Mengikuti (facts,
//                            Lengkapi Data, Go/No-Go)
//                            and Tempel Pengumuman LPSE (the intake,
//                            penyedia/PaketIntake.tsx)
//                          3 Unggah dan Baca Dokumen Paket (files per
//                            document type, penyedia/PaketDocs.tsx ->
//                            Susun Matriks)
//                          4 Analisa Persyaratan dan Kesiapan Penawaran
//   Pelaksanaan Pemilihan  1 Reviu Dokumen Pemilihan dan Matriks Kepatuhan
//                          2 Buat Penawaran yang Diminta Pokja (templates
//                            under Administrasi, Kualifikasi, Teknis, Harga)
//                          3 Reviu Akhir Sebelum Submit Penawaran (Quality Gate)
//                          4 Klarifikasi, Sanggah dan Tindak Lanjut
//   Pelaksanaan Kontrak    1 Kesiapan Kontrak dan Mobilisasi
//                          2 Kendalikan Mutu, Waktu, Biaya dan Risiko
//                          3 Dokumen Utama Selama Pelaksanaan
//                          4 Catat Pengendalian Kontrak (the monitoring tool)
//   Serah Terima           Serah Terima Hasil Pekerjaan, Checklist Serah Terima
//
// Reviu Persyaratan, Quality Gate and Kalkulator RAB open as full work views
// (onTool) in place of the stage, the way v4 opens them as their own pages.
import { useState, type ReactNode } from "react";
import * as API from "../api";
import type { ChecklistGroup, ChecklistStage, ProjectDetail, ProjectFile } from "../api";
import { formatRupiah } from "../lib/format";
import { canDocx, useDocx } from "../pbj/docx";
import { Opportunities } from "../penyedia/Opportunities";
import { PaketDocs } from "../penyedia/PaketDocs";
import { PaketIntakeDialog } from "../penyedia/PaketIntake";
import { useFeatures } from "../shell/features";
import { Link } from "../router";
import { useAsync } from "../ui/components";
import { Glyph } from "../ui/icons";
import { JENIS_LABEL, KUALIFIKASI_LABEL, METODE_LABEL, STAGE_LABEL, STEPS, formatDeadline } from "./stages";
import * as Work from "./work/api";
import { PengendalianKontrak } from "./work/PengendalianKontrak";
import { DocGrid, stageTemplates } from "./work/StageDocs";
import { canActivityDocs } from "./ActivityDocs";

export type WorkTool = "reviu" | "quality" | "rab";

export const TOOL_LABEL: Record<WorkTool, string> = {
  reviu: "Reviu Persyaratan",
  quality: "Quality Gate",
  rab: "Kalkulator RAB",
};

/** What each stage is about, under its tab (v4 PLUTO_STAGES). */
export const STAGE_SUB: Record<ChecklistStage, string> = {
  persiapan_pemilihan: "Temukan paket, bangun project, baca dokumen",
  pelaksanaan_pemilihan: "Penuhi syarat, susun penawaran, reviu akhir",
  pelaksanaan_kontrak: "Kelola pekerjaan, progres, perubahan, pembayaran",
  serah_terima: "Pemeriksaan, BAST, pemeliharaan",
};

/** Go/No-Go (S03) and the requirement matrix (S01) in the Penyedia template catalog. */
export const GO_NO_GO_TEMPLATE = "S03";
export const MATRIX_TEMPLATE = "S01";

// ---------------------------------------------------------------------------
// Stage tabs
// ---------------------------------------------------------------------------
export interface StageTabsProps {
  /** The paket's own stage (status); ditutup and selesai mark no tab as current. */
  current: string;
  viewed: ChecklistStage;
  checklist: ChecklistGroup[];
  onView: (stage: ChecklistStage) => void;
}

export function StageTabs({ current, viewed, checklist, onView }: StageTabsProps) {
  return (
    <ol className="pks-tabs" aria-label="Tahap paket">
      {STEPS.map((s, i) => {
        const stage = s.stage as ChecklistStage;
        const items = checklist.find((g) => g.stage === stage)?.items ?? [];
        const done = items.filter((x) => x.done).length;
        return (
          <li key={stage}>
            <button
              type="button"
              className="pks-tab"
              aria-pressed={viewed === stage}
              data-current={current === stage ? "" : undefined}
              onClick={() => onView(stage)}
            >
              <span className="pks-tab-top">
                <span className="pks-tab-no num">Tahap {i + 1}</span>
                {current === stage ? <span className="pks-tab-now">Tahap paket</span> : null}
              </span>
              <span className="pks-tab-label">{s.label}</span>
              <span className="pks-tab-sub">{STAGE_SUB[stage]}</span>
              <span className="pks-tab-count num">
                {done}/{items.length} aktivitas dicentang
              </span>
            </button>
          </li>
        );
      })}
    </ol>
  );
}

// ---------------------------------------------------------------------------
// Section frame
// ---------------------------------------------------------------------------
function Section({ id, no, title, lead, action, children }: { id: string; no?: number; title: string; lead: ReactNode; action?: ReactNode; children?: ReactNode }) {
  return (
    <section className="pks" aria-labelledby={`pks-${id}-title`} data-section={id}>
      <div className="pks-head">
        <div className="pks-head-text">
          <h3 id={`pks-${id}-title`} className="pks-title">
            {no ? <span className="pks-no num">{no}</span> : null}
            <span>{title}</span>
          </h3>
          <p className="pks-lead">{lead}</p>
        </div>
        {action ? <div className="pks-head-action">{action}</div> : null}
      </div>
      {children}
    </section>
  );
}

function Steps({ label, steps }: { label: string; steps: [string, string][] }) {
  return (
    <ol className="wk-steps" aria-label={label}>
      {steps.map(([title, desc], i) => (
        <li key={title} className="wk-step">
          <span className="wk-step-no num">{i + 1}</span>
          <span className="wk-step-text">
            <strong>{title}</strong>
            <span>{desc}</span>
          </span>
        </li>
      ))}
    </ol>
  );
}

function ToolButton({ tool, label, onTool, primary = true }: { tool: WorkTool; label: string; onTool: (t: WorkTool) => void; primary?: boolean }) {
  return (
    <button type="button" className={`btn ${primary ? "btn-primary" : "btn-secondary"}`} onClick={() => onTool(tool)}>
      <span>{label}</span>
      <Glyph name="chevronRight" size={18} />
    </button>
  );
}

/** Unduh Word of one Penyedia template filled from this paket. */
function TemplateDocx({ projectId, templateId, label }: { projectId: string; templateId: string; label: string }) {
  const features = useFeatures();
  const docx = useDocx();
  if (features.list === null || !canDocx(features)) return null;
  return (
    <>
      <button
        type="button"
        className="btn btn-secondary"
        disabled={docx.busy !== null}
        aria-busy={docx.busy !== null || undefined}
        onClick={() => void docx.start(templateId, () => API.downloadTemplateDocx(templateId, projectId))}
      >
        <Glyph name="documentDownload" size={18} />
        <span>{docx.busy ? "Menyiapkan" : label}</span>
      </button>
      {docx.error ? (
        <span className="pk-item-error" role="alert">
          <Glyph name="danger" size={16} />
          <span>{docx.error.message}</span>
        </span>
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------------------
// Stage view
// ---------------------------------------------------------------------------
export interface StageViewProps {
  detail: ProjectDetail;
  viewed: ChecklistStage;
  /** The Penyedia template library (empty while it loads or without the feature). */
  templates: API.TemplateItem[];
  /** The viewed stage's checklist, rendered by the page. */
  checklist: ReactNode;
  checklistCount: number;
  onTool: (tool: WorkTool) => void;
  onView: (stage: ChecklistStage) => void;
  /** Lengkapi Data: the paket info dialog. */
  onEdit: () => void;
  /** Tempel Pengumuman LPSE: the confirmed intake (project and checklist); without it the button is hidden. */
  onIntake?: (next: ProjectDetail) => void;
}

export function StageView(props: StageViewProps) {
  const { viewed, checklist, checklistCount } = props;
  let body: ReactNode;
  if (viewed === "persiapan_pemilihan") body = <Persiapan {...props} />;
  else if (viewed === "pelaksanaan_pemilihan") body = <Pemilihan {...props} />;
  else if (viewed === "pelaksanaan_kontrak") body = <Kontrak {...props} />;
  else body = <SerahTerima {...props} />;
  return (
    <div className="pks-stage" data-stage={viewed}>
      {body}
      {viewed !== "serah_terima" ? (
        <details className="pks-details">
          <summary>
            <Glyph name="chevronRight" size={16} />
            <span>Detail aktivitas relevan ({checklistCount} proses)</span>
          </summary>
          <p className="pks-caption">Daftar ini referensi dari basis data proses Penyedia, bukan seluruhnya dokumen wajib untuk setiap paket. Centang setelah buktinya Anda periksa.</p>
          {checklist}
        </details>
      ) : null}
    </div>
  );
}

function useRequirements(projectId: string) {
  return useAsync(() => Work.getRequirements(projectId).catch(() => null), [projectId]);
}

function Persiapan({ detail, onTool, onEdit, onIntake }: StageViewProps) {
  const p = detail.project;
  const matrix = useRequirements(p.id);
  const [intake, setIntake] = useState(false);
  // The intake stores its files as dokumen pemilihan: the document list reads again.
  const [filesTick, setFilesTick] = useState(0);
  const metode = p.metode ? METODE_LABEL[p.metode] + (p.kualifikasi ? `, ${KUALIFIKASI_LABEL[p.kualifikasi]}` : "") : "Belum diisi";
  const facts: [string, string][] = [
    ["Instansi", p.instansi || "Belum diisi"],
    ["Kode SPSE / RUP", [p.kodePaket, p.kodeRup].filter(Boolean).join(" / ") || "Belum diisi"],
    ["Jenis dan metode", `${p.jenisPengadaan ? JENIS_LABEL[p.jenisPengadaan] : "Jenis belum diisi"}, ${metode}`],
    ["Nilai pagu / HPS", `${p.pagu == null ? "Belum diisi" : formatRupiah(p.pagu)} / ${p.nilaiHps == null ? "Belum diisi" : formatRupiah(p.nilaiHps)}`],
    ["Tenggat penawaran", p.deadline ? `${formatDeadline(p.deadline)}${p.deadlineTime ? `, ${p.deadlineTime}` : ""}` : "Wajib periksa SPSE"],
    ["PIC penawaran", p.pic || "Belum ditunjuk"],
  ];
  return (
    <>
      <Opportunities headingLevel={3} />
      <Section
        id="identitas"
        no={1}
        title="Data Dasar Paket dan Keputusan Mengikuti"
        lead="Simpan identitas paket resmi dan tentukan kesiapan internal sebelum mengikuti pemilihan."
        action={
          <>
            {onIntake ? (
              <button type="button" className="btn btn-primary" aria-haspopup="dialog" onClick={() => setIntake(true)}>
                <Glyph name="magicStar" size={18} />
                <span>Tempel Pengumuman LPSE</span>
              </button>
            ) : null}
            <button type="button" className="btn btn-secondary" aria-haspopup="dialog" onClick={onEdit}>
              <Glyph name="edit2" size={18} />
              <span>Lengkapi Data</span>
            </button>
          </>
        }
      >
        <dl className="pks-facts">
          {facts.map(([k, v]) => (
            <div key={k} className="pks-fact">
              <dt>{k}</dt>
              <dd>{v}</dd>
            </div>
          ))}
        </dl>
        <p className="pks-caption">
          Go/No-Go: cek kesesuaian bidang usaha, pengalaman, ketersediaan tenaga dan peralatan, arus kas, lokasi, jadwal, dan risiko kontrak. Keputusan akhir ada pada perusahaan.
        </p>
        <div className="wk-actions">
          <TemplateDocx projectId={p.id} templateId={GO_NO_GO_TEMPLATE} label="Unduh Matriks Go/No-Go" />
          {p.sumberUrl ? (
            <a className="btn btn-secondary" href={p.sumberUrl} target="_blank" rel="noopener noreferrer nofollow">
              <Glyph name="link" size={18} />
              <span>Buka sumber paket resmi</span>
            </a>
          ) : null}
        </div>
      </Section>
      {onIntake ? (
        <PaketIntakeDialog
          open={intake}
          projectId={p.id}
          onClose={() => setIntake(false)}
          onFiles={() => setFilesTick((n) => n + 1)}
          onSaved={(next) => {
            setIntake(false);
            onIntake(next);
          }}
        />
      ) : null}
      <SourceDocs projectId={p.id} tick={filesTick} onTool={onTool} />
      <Section
        id="keputusan"
        no={3}
        title="Analisa Persyaratan dan Kesiapan Penawaran"
        lead="Ekstraksi awal mengelompokkan administrasi, kualifikasi, teknis, dan harga. Validasi manusia tetap wajib."
        action={<ToolButton tool="reviu" label="Buka Matriks Kepatuhan" onTool={onTool} />}
      >
        <Steps
          label="Langkah keputusan mengikuti"
          steps={[
            ["Cek versi dokumen", "LDP/LDK, IKP, KAK, spesifikasi, lampiran, serta semua adendum."],
            ["Daftar syarat minimum", "Temukan syarat kualifikasi, legalitas, teknis, harga, jaminan, dan dokumen wajib."],
            ["Siapkan bukti pendukung", "Tautkan setiap persyaratan dengan file atau bukti nyata dan PIC perusahaan."],
            ["Tentukan Go / No-Go", "Nilai kemampuan pelaksanaan, risiko, tenggat, dan peluang secara jujur."],
          ]}
        />
        <p className="pks-caption num">{matrix.data ? `${matrix.data.summary.total} butir persyaratan saat ini berada dalam matriks.` : "Matriks persyaratan belum termuat."}</p>
      </Section>
    </>
  );
}

/** Unggah dan Baca Dokumen Paket: the paket documents by type (penyedia/PaketDocs.tsx), each readable one a source for the requirement matrix. */
function SourceDocs({ projectId, tick, onTool }: { projectId: string; tick: number; onTool: (t: WorkTool) => void }) {
  const [own, setOwn] = useState(0);
  const files = useAsync(() => API.listProjectFiles(projectId), [projectId, tick, own]);

  const analyze = async (file: ProjectFile) => {
    await Work.extractRequirements(projectId, { fileId: file.driveFileId, sourceName: file.name.slice(0, 200) });
    onTool("reviu");
  };

  return (
    <Section
      id="dokumen-sumber"
      no={2}
      title="Unggah dan Baca Dokumen Paket"
      lead="Unggah per jenis: Dokumen Pemilihan, Spesifikasi Teknis/KAK, BoQ, Draft Kontrak, dan dokumen lainnya. Pluto membaca teksnya untuk menyusun matriks persyaratan."
    >
      <PaketDocs projectId={projectId} view={files.data} error={files.error} onChanged={() => setOwn((n) => n + 1)} onAnalyze={analyze} />
      <div className="wk-actions">
        <button type="button" className="btn btn-secondary" onClick={() => onTool("reviu")}>
          <span>Lihat dan Koreksi Matriks</span>
          <Glyph name="chevronRight" size={18} />
        </button>
      </div>
    </Section>
  );
}

/** The four parts of an offer, the same split as the requirement matrix (Reviu Persyaratan). */
export const OFFER_CATEGORIES: { id: "administrasi" | "kualifikasi" | "teknis" | "harga"; label: string }[] = [
  { id: "administrasi", label: "Administrasi" },
  { id: "kualifikasi", label: "Kualifikasi" },
  { id: "teknis", label: "Teknis" },
  { id: "harga", label: "Harga" },
];

/** Buat Penawaran: the offer templates under their category; an empty category says so. */
function OfferGroups({ projectId, groups }: { projectId: string; groups: { id: string; label: string; items: API.TemplateItem[] }[] }) {
  const features = useFeatures();
  // The same gate as DocGrid: nothing to do with a template without Unduh Word or drafting.
  const usable = features.list !== null && (canDocx(features) || canActivityDocs(features));
  if (!usable || groups.every((g) => g.items.length === 0)) return null;
  return (
    <div className="pyo">
      {groups.map((g) => (
        <section key={g.id} className="pyo-group" aria-labelledby={`pyo-${g.id}-title`} data-category={g.id}>
          <h4 id={`pyo-${g.id}-title`} className="pyo-title">
            <span>{g.label}</span>
            <span className="pyd-count num">{g.items.length}</span>
          </h4>
          {g.items.length === 0 ? <p className="pyd-empty">Belum ada template {g.label.toLowerCase()} untuk paket ini.</p> : <DocGrid projectId={projectId} templates={g.items} label={`Dokumen penawaran ${g.label.toLowerCase()}`} />}
        </section>
      ))}
    </div>
  );
}

function Pemilihan({ detail, templates, onTool, onView }: StageViewProps) {
  const p = detail.project;
  const matrix = useRequirements(p.id);
  const s = matrix.data?.summary;
  const offer = OFFER_CATEGORIES.map((c) => ({ ...c, items: stageTemplates(templates, "pelaksanaan_pemilihan", p, [c.id], 9) }));
  const result = stageTemplates(templates, "pelaksanaan_pemilihan", p, ["sanggah", "banding", "klarifikasi"], 7);
  return (
    <>
      <Section
        id="reviu"
        no={1}
        title="Review Dokumen Pemilihan dan Matriks Kepatuhan"
        lead="Periksa kualifikasi, administrasi, teknis, dan harga terhadap bukti yang benar-benar dimiliki."
        action={<ToolButton tool="reviu" label="Buka Reviu" onTool={onTool} />}
      >
        <p className="pks-stat">
          <strong className="num">{s ? `${s.sesuai} / ${s.total}` : "0 / 0"}</strong>
          <span>persyaratan ditandai sesuai berdasarkan pemeriksaan Anda{s && s.belumSesuai > 0 ? `, ${s.belumSesuai} masih belum sesuai` : ""}</span>
        </p>
        <div className="wk-actions">
          <button type="button" className="btn btn-secondary" onClick={() => onView("persiapan_pemilihan")}>
            <Glyph name="arrowLeft" size={18} />
            <span>Kembali unggah dokumen</span>
          </button>
          <TemplateDocx projectId={p.id} templateId={MATRIX_TEMPLATE} label="Ekspor Matriks Word" />
        </div>
      </Section>
      <Section
        id="penawaran"
        no={2}
        title="Buat Penawaran yang Diminta Pokja"
        lead="Template dasar perlu disesuaikan dengan format, kriteria evaluasi, dan adendum paket resmi."
        action={
          <Link href="/template" className="btn btn-secondary">
            <span>Lihat Semua Template</span>
          </Link>
        }
      >
        <OfferGroups projectId={p.id} groups={offer} />
        <div className="wk-actions">
          <ToolButton tool="rab" label="Hitung Rincian Harga (RAB)" onTool={onTool} primary={false} />
        </div>
      </Section>
      <Section
        id="quality"
        no={3}
        title="Reviu Akhir Sebelum Submit Penawaran"
        lead="Periksa surat, legalitas, teknis, harga, adendum, tenggat, format, dan bukti sebelum pengiriman di SPSE."
        action={<ToolButton tool="quality" label="Periksa Penawaran" onTool={onTool} />}
      >
        <Steps
          label="Pemeriksaan akhir"
          steps={[
            ["Dokumen terbaru", "Tidak ada syarat atau adendum yang tertinggal."],
            ["Bukti setiap syarat", "Tersedia dan bisa ditelusuri, bukan hanya pernyataan."],
            ["Harga dan masa berlaku", "Sesuai format dan masa berlaku dalam dokumen paket."],
            ["Unggah resmi", "Penawaran hanya disampaikan melalui kanal resmi dan sebelum tenggat."],
          ]}
        />
      </Section>
      <Section
        id="hasil"
        no={4}
        title="Klarifikasi, Sanggah, dan Tindak Lanjut"
        lead="Gunakan hanya prosedur yang tersedia untuk metode dan jenis paket, sesuai jadwal resmi."
      >
        <DocGrid projectId={p.id} templates={result} label="Dokumen klarifikasi dan sanggah" />
        <p className="pks-caption">Sanggah banding hanya untuk Tender Pekerjaan Konstruksi sesuai ketentuan. Tidak semua peserta atau metode berhak mengajukan sanggah.</p>
      </Section>
    </>
  );
}

function Kontrak({ detail, templates }: StageViewProps) {
  const p = detail.project;
  return (
    <>
      <Section
        id="sebelum-mulai"
        no={1}
        title="Kesiapan Kontrak dan Mobilisasi"
        lead="Cocokkan SPPBJ atau pesanan, rancangan kontrak final, jaminan, jadwal, PIC, tenaga dan peralatan, serta serah lokasi bila berlaku."
      >
        <DocGrid projectId={p.id} templates={stageTemplates(templates, "pelaksanaan_kontrak", p, ["kontrak"], 4)} label="Dokumen kesiapan kontrak" />
      </Section>
      <Section
        id="pengendalian"
        no={2}
        title="Kendalikan Mutu, Waktu, Biaya, dan Risiko"
        lead="Rekam progres terhadap baseline, deviasi, instruksi PPK, bukti pekerjaan, mutu, perubahan, kendala, dan tindak lanjut."
      >
        <Steps
          label="Aspek pengendalian kontrak"
          steps={[
            ["Waktu", "Jadwal, milestone, deviasi, dan rencana pemulihan."],
            ["Mutu dan volume", "Hasil uji, inspeksi, bukti kuantitas, dan penerimaan."],
            ["Biaya dan pembayaran", "Termin, nilai prestasi, tagihan, dan potongan menurut kontrak."],
            ["Perubahan dan risiko", "Notifikasi dini, dasar perubahan, persetujuan, klaim, dan sengketa."],
          ]}
        />
        <DocGrid projectId={p.id} templates={stageTemplates(templates, "pelaksanaan_kontrak", p, ["tagihan"], 3)} label="Dokumen tagihan" />
      </Section>
      <Section
        id="output"
        no={3}
        title="Dokumen Utama Selama Pelaksanaan"
        lead="Buat draf tepat saat kejadiannya. Perubahan kontrak wajib memenuhi persyaratan dan persetujuan pihak berwenang."
      >
        <DocGrid projectId={p.id} templates={stageTemplates(templates, "pelaksanaan_kontrak", p, [], 12)} label="Dokumen pelaksanaan kontrak" />
      </Section>
      <PengendalianKontrak projectId={p.id} />
    </>
  );
}

function SerahTerima({ detail, templates, checklist, checklistCount }: StageViewProps) {
  const p = detail.project;
  const items = detail.checklist.find((g) => g.stage === "serah_terima")?.items ?? [];
  const done = items.filter((i) => i.done).length;
  return (
    <>
      <Section
        id="serah"
        title="Serah Terima Hasil Pekerjaan"
        lead="Pengajuan pemeriksaan, bukti kesesuaian, berita acara, tagihan akhir, serta garansi dan pemeliharaan bila berlaku."
        action={
          <span className="pks-badge num">
            {done} / {checklistCount} proses
          </span>
        }
      >
        <DocGrid projectId={p.id} templates={stageTemplates(templates, "serah_terima", p, [], 9)} label="Dokumen serah terima" />
      </Section>
      <section className="pks" aria-labelledby="pks-checklist-serah-title" data-section="checklist-serah">
        <div className="pks-head">
          <div className="pks-head-text">
            <h3 id="pks-checklist-serah-title" className="pks-title">
              Checklist Serah Terima
            </h3>
            <p className="pks-lead">Aktivitas relevan dari basis data proses Penyedia. Centang setelah bukti nyata diperiksa.</p>
          </div>
        </div>
        {checklist}
      </section>
    </>
  );
}

/** The note under every stage (v4's closing line). */
export const STAGE_DISCLAIMER =
  "Pluto tidak terhubung ke akun SPSE. Semua kelengkapan penawaran dan pelaksanaan harus ditetapkan dari dokumen resmi dan bukti aktual; Pluto tidak menjamin kemenangan atau kelulusan evaluasi.";

/** "2 dari 6 selesai". */
export function progressText(items: { done: boolean }[]): string {
  return `${items.filter((i) => i.done).length} dari ${items.length} selesai`;
}

/**
 * The heading over the viewed stage: "Tahap <stage>" with its done count;
 * "Paket ditutup" or "Paket selesai" on the stage an end status opens on;
 * a note when the viewed stage is not the paket's own.
 */
export function StageHeading({ viewed, current, items }: { viewed: ChecklistStage; current: string; items: { done: boolean }[] }) {
  const own = viewed === current;
  const ended = (current === "ditutup" && viewed === "pelaksanaan_pemilihan") || (current === "selesai" && viewed === "serah_terima");
  return (
    <div className="pk-stage">
      <div className="pk-stage-head">
        <h2 id="pk-stage-title" className="pk-stage-title">
          {ended ? (current === "ditutup" ? "Paket ditutup" : "Paket selesai") : `Tahap ${STAGE_LABEL[viewed]}`}
        </h2>
        <span className="pk-stage-count num">{progressText(items)}</span>
      </div>
      <p className="pk-stage-hint">{STAGE_SUB[viewed]}.</p>
      {!own && current in STAGE_SUB ? (
        <p className="pks-caption">Anda sedang melihat tahap ini. Tahap paket saat ini: {STAGE_LABEL[current as ChecklistStage]}.</p>
      ) : null}
    </div>
  );
}
